import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, ListCheck, Loader } from "lucide-react";
import { verifyPrefillDetails } from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isNumeric, isAlpha } from "validator";

function Prefill() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isNumber = (value) => {
    if (!value) return false;

    return isNumeric(value, { no_symbols: true });
  };

  const isAlphabetic = (value) => {
    if (!value) return;

    return isAlpha(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(setIsJsonCopied(true));
    showSuccessToast("Text Copied");
  };
  // ---------------- VERIFY CONSUMER ----------------
  const handleVerifyConsumer = async () => {
    if (!profileData) return;

    const validateNumber = isNumber(mobile);
    const validateName = isAlphabetic(name);

    if (!validateNumber) {
      return setInputError("⚠️ Please enter number only in Mobile No. field");
    } else if (mobile.length < 10) {
      return setInputError("⚠️ Enter at least 10 digits in Mobile No. field");
    } else if (!validateName) {
      return setInputError("⚠️ Enter Character Only In Full Name");
    } else if (name.length < 3) {
      return setInputError(
        "⚠️ Enter at least 3 characters in first Name field",
      );
    }

    try {
      setIsLoading(true);
      setResponse(null);
      setInputError(null);

      const res = await verifyPrefillDetails({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        mobile: mobile.trim(),
        name: name.trim(),
      });

      if (res) {
        apiResponse = res;
        setResponse(apiResponse);
        console.log(res);
      }

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "request successful");
      } else if (res?.status?.code === 422) {
        showErrorToast(response?.message || "Invalid format");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!response.success) {
        showErrorToast(response?.data?.message || "verificaton failed");
      } else if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else {
        showErrorToast(res?.status?.message || "Verification failed");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center py-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md p-4 sm:p-6 mt-6 bg-white">
          <div className="flex flex-wrap gap-4">
            {/* Mobile */}
            <div className="flex items-center gap-4">
              <label className=" font-bold">
                Mobile No <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={mobile}
                maxLength={10}
                onChange={(e) => setMobile(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: 9187864534"
              />
            </div>

            {/*  Name */}
            <div className="flex items-center gap-4">
              <label className=" font-bold">
                Full Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: Johnshon Charles"
              />
            </div>

            {/* input errors */}

            {inputError && (
              <div className="text-red-700 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex justify-end mt-2">
              <button
                disabled={isLoading || !mobile || !name}
                onClick={handleVerifyConsumer}
                className="px-6 py-2 bg-gradient-to-br active:from-green-600 active:to-green-700 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div>
          <div className="bg-gray-200 border border-gray-400 p-5 mt-12 rounded-md">
            <h3 className="font-bold text-lg mb-2">
              ‼️ Important Instructions <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid mobile number and Full Name. clicking the
              Verify button. After successful verification, the system will
              return the response in structured format, including contact
              details associated with the provided details. You may review and
              copy the data for your reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- USER DETAILS -------- */}
            <div className="bg-gray-200 p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2  font-bold text-blue-500 mb-1">
                <ListCheck /> All Details
              </h2>
              <hr className="mb-8" />

              {/* BASIC INFO */}
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-600 w-44 inline-block">
                    Name:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.name || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-44 inline-block">
                    Mobile:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.mobile || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-44 inline-block">
                    Age:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.personal_info?.age || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-44 inline-block">
                    Gender:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.personal_info?.gender || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-44 inline-block">
                    Total Income:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.personal_info?.total_income || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-600 w-44 inline-block">
                    Occupation:
                  </span>
                  <span className="text-gray-900">
                    {response?.data?.personal_info?.occupation || "N/A"}
                  </span>
                </div>
              </div>

              {/* PHONE INFO */}
              <h3 className="font-bold text-xl text-gray-800 pt-6 pb-3">
                Phone Info
              </h3>

              <div className="space-y-3">
                {response?.data?.phone_info?.length > 0 &&
                  response.data.phone_info.map((phone_info, index) => (
                    <div
                      key={index}
                      className="border p-4 rounded-md bg-blue-500 text-white"
                    >
                      <div>
                        <span className="font-semibold text-blue-200 w-40 inline-block">
                          Reported Date:
                        </span>
                        {phone_info.reported_date}
                      </div>

                      <div>
                        <span className="font-semibold text-blue-200 w-40 inline-block">
                          Type:
                        </span>
                        {phone_info.type_code}
                      </div>

                      <div>
                        <span className="font-semibold text-blue-200 w-40 inline-block">
                          Number:
                        </span>
                        {phone_info.number}
                      </div>
                    </div>
                  ))}
              </div>

              {/* ADDRESS INFO */}
              {response?.status?.code === 200 &&
                response?.data?.address_info?.length > 0 && (
                  <div className="w-full max-w-6xl mt-8">
                    <h3 className="font-bold text-xl text-gray-800 mb-4">
                      Addresses
                    </h3>

                    <div className="space-y-4">
                      {response.data.address_info.map((addr, index) => (
                        <div
                          key={index}
                          className="border p-4 rounded-md bg-blue-500 text-white"
                        >
                          <p>{addr.address}</p>

                          <div className="mt-2">
                            <span className="font-semibold text-blue-200">
                              State:
                            </span>{" "}
                            {addr.state}
                          </div>

                          <div>
                            <span className="font-semibold text-blue-200">
                              Type:
                            </span>{" "}
                            {addr.type}
                          </div>

                          <div>
                            <span className="font-semibold text-blue-200">
                              Postal Code:
                            </span>{" "}
                            {addr.postal}
                          </div>

                          <div className="text-xs text-blue-200 mt-2">
                            Reported on: {addr.reported_date}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* IDENTITY INFO */}
              {(response?.data?.email_info ||
                response?.data?.identity_info) && (
                <div className="mt-8">
                  <h3 className="font-bold text-xl text-gray-800 mb-3">
                    Identity Info
                  </h3>

                  <div className="border p-4 rounded-md bg-blue-500 text-white space-y-1">
                    {response?.data?.email_info?.length > 0 &&
                      response.data.email_info.map((email, i) => (
                        <div key={i}>
                          <span className="font-semibold text-blue-200 w-40 inline-block">
                            Email:
                          </span>
                          {email || "N/A"}
                        </div>
                      ))}

                    {response?.data?.identity_info?.pan_number?.length > 0 &&
                      response.data.identity_info.pan_number.map((pan, i) => (
                        <div key={i}>
                          <span className="font-semibold text-blue-200 w-40 inline-block">
                            PAN No:
                          </span>
                          {pan.id_number || "N/A"}
                        </div>
                      ))}

                    {response?.data?.identity_info?.passport_number?.length >
                      0 &&
                      response.data.identity_info.passport_number.map(
                        (passport, i) => (
                          <div key={i}>
                            <span className="font-semibold text-blue-200 w-40 inline-block">
                              Passport ID:
                            </span>
                            {passport.id_number || "N/A"}
                          </div>
                        ),
                      )}

                    {response?.data?.identity_info?.ration_card?.length > 0 &&
                      response.data.identity_info.ration_card.map(
                        (ration, i) => (
                          <div key={i}>
                            <span className="font-semibold text-blue-200 w-40 inline-block">
                              Ration Card:
                            </span>
                            {ration.id_number || "N/A"}
                          </div>
                        ),
                      )}

                    {response?.data?.identity_info?.driving_license?.length >
                      0 &&
                      response.data.identity_info.driving_license.map(
                        (license, i) => (
                          <div key={i}>
                            <span className="font-semibold text-blue-200 w-40 inline-block">
                              License No:
                            </span>
                            {license.id_number || "N/A"}
                          </div>
                        ),
                      )}

                    {response?.data?.identity_info?.voter_id?.length > 0 &&
                      response.data.identity_info.voter_id.map((voterid, i) => (
                        <div key={i}>
                          <span className="font-semibold text-blue-200 w-40 inline-block">
                            Voter ID:
                          </span>
                          {voterid.id_number || "N/A"}
                        </div>
                      ))}

                    {response?.data?.identity_info?.aadhar_number?.length > 0 &&
                      response.data.identity_info.aadhar_number.map(
                        (aadhar, i) => (
                          <div key={i}>
                            <span className="font-semibold text-blue-200 w-40 inline-block">
                              Aadhar No:
                            </span>
                            {aadhar.id_number || "N/A"}
                          </div>
                        ),
                      )}

                    {response?.data?.identity_info?.other_id?.length > 0 &&
                      response.data.identity_info.other_id.map((otherid, i) => (
                        <div key={i}>
                          <span className="font-semibold text-blue-200 w-40 inline-block">
                            Other ID:
                          </span>
                          {otherid.id_number || "N/A"}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Prefill;
